import {
  CloseOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { BackButton, Button, Heading } from '@mm/components';
import { InfoCircleSolidIcon } from '@mm/icons';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { CampaignType } from '@mm/state/model/CampaignsModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  CampaignTypeWithMappedPurpose,
  selectCampaignById,
} from '@mm/state/selectors/campaignSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import css from './AutomationSoLDSMSNotificationSettings.module.css';
import { ReactComponent as SmsPhoneIcon } from 'assets/icons/sms-phone.svg';
import { useViewActions } from '@mm/state/actions/viewActions';
import { Editor } from '../SlideoutEmail/editor/components/Editor';
import { EditorState } from 'prosemirror-state';
import { set, cloneDeep, isEqual } from 'lodash';
import { StoreState } from '@mm/state/store-interfaces';
import { PhoneInput } from '../../../../../common/components/PhoneInput/PhoneInput';
import { colors } from '@mm/utils';
import { randomId } from '../../../../../common/helpers/utils';
import {
  getHTMLStringFromState,
  getHTMLText,
} from '../SlideoutEmail/editor/utils';
import { soldConfigInitialValues } from '../AutomationSoLDSettingsSlideout/AutomationSoLDSettingsSlideout';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Schema } from 'prosemirror-model';
import { nodes as basicNodes } from 'prosemirror-schema-basic';
import { listNodes } from '../SlideoutEmail/editor/nodes';
import { SlideOutMenuType } from '@mm/state/interfaces/view';

export type IAutomationSoLDSMSNotificationSettings = {
  menuType: SlideOutMenuType;
  className?: string;
  campaignId?: number;
};

// this is done just to prevent rich text paste into editor
const schema = new Schema({
  nodes: {
    doc: basicNodes.doc,
    text: basicNodes.text,
    paragraph: basicNodes.paragraph,
    ...listNodes,
  },
});

const getInitialPhones = (campaign?: CampaignTypeWithMappedPurpose) => {
  const to = campaign?.soldConfigurations?.unicorn?.notifications?.sms?.to;

  return to?.length
    ? to.map(value => ({
        value,
        id: randomId(),
        isValid: true,
      }))
    : [{ value: '', id: randomId(), isValid: false }];
};

export const AutomationSoLDSMSNotificationSettings: React.FC<
  IAutomationSoLDSMSNotificationSettings
> = props => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { campaignId = -1 } = props;

  const campaign = useSelector(state => selectCampaignById(state, campaignId));

  const campaignCRUDActions = useCampaignCRUDActions();

  const [busy, setBusy] = useState(false);

  const [content, setContent] = useState<EditorState>();

  const [phones, setPhones] = useState(getInitialPhones(campaign));

  const onAddPhoneNumberClick = () => {
    setPhones([...phones, { value: '', isValid: false, id: randomId() }]);
  };

  const attributes = useSelector(
    (state: StoreState) => state.attribute.entities,
  );

  const isSaveDisabled = !phones.every(phone => phone.isValid) && !busy;

  const getInitialState = () =>
    cloneDeep(campaign?.soldConfigurations || soldConfigInitialValues);

  const getPayload = () => {
    const initials = getInitialState();

    if (phones) {
      set(
        initials,
        'unicorn.notifications.sms.to',
        phones.map(v => v.value).filter(Boolean),
      );
    }

    if (content) {
      set(
        initials,
        'unicorn.notifications.sms.content',
        getHTMLStringFromState(content),
      );
    }
    return initials;
  };

  const initialState = useMemo(() => getInitialState(), [campaign]);

  const onSave = async () => {
    setBusy(true);

    const payload = getPayload();

    if (!getHTMLText(payload.unicorn.notifications.sms.content)) {
      return errorToast('Please enter text message');
    }

    const invalidPhones = payload?.unicorn?.notifications?.sms?.to?.some(
      phone =>
        !['US', 'CA'].includes(
          parsePhoneNumberFromString(phone)?.country || '',
        ),
    );

    if (invalidPhones) {
      return errorToast('US & Canada numbers allowed only');
    }

    try {
      if (campaignId) {
        await campaignCRUDActions.updateCampaign(
          {
            id: campaignId,
            soldConfigurations: payload,
          } as unknown as CampaignType,
          chatbotId,
        );

        successToast('Your changes have been saved.', 'top-left');

        closeSlideout();
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const modalActions = useModalActions();

  const onBeforeClose = async () => {
    const payload = getPayload();

    const hasChanged = !isEqual(
      initialState.unicorn.notifications.sms,
      payload.unicorn.notifications?.sms,
    );

    if (hasChanged) {
      return new Promise((resolve, reject) => {
        modalActions.openGeneralConfirmModal({
          headerText: 'You have unsaved changes',
          messageText: 'Are you sure that you want to leave without saving?',
          onCloseHandler: ok => {
            if (ok) {
              resolve(true);
            } else {
              reject();
            }
          },
          okButtonText: "Yes I'm sure",
          cancelButtonText: 'Cancel',
        });
      });
    }

    return Promise.resolve(true);
  };

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
    } catch (err) {}
  };

  const { setRef, close: closeSlideout } = useSlideoutOnClose(
    onBeforeClose,
    props.menuType,
  );

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <BackButton onClick={close} />
        <Heading size={3}>SMS Notifications</Heading>

        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <div className={css.phoneRow}>
          <div className={css.phoneField}>
            <SmsPhoneIcon />
            <div className={css.subtitle}>Send to:</div>
          </div>

          <div className={css.phoneInputContainer}>
            {phones.map((phone, idx) => (
              <div className={css.phoneInputRow} key={phone.id}>
                <PhoneInput
                  className={css.phoneInput}
                  onChange={(value, isValid) => {
                    const dup = [...phones];
                    dup[idx] = { value, isValid, id: phone.id };
                    setPhones(dup);
                  }}
                  value={phones[idx].value}
                />

                {!!idx && (
                  <DeleteOutlined
                    onClick={() => {
                      const dup = [...phones];
                      dup.splice(idx, 1);
                      setPhones(dup);
                    }}
                  />
                )}
              </div>
            ))}

            <div className={css.row}>
              <Button
                color="link"
                disabled={phones.length >= 3}
                onClick={onAddPhoneNumberClick}
                leftIcon={<PlusCircleOutlined color={colors.accentBlue} />}
              >
                {' '}
                Add phone number
              </Button>

              <InfoCircleSolidIcon className={css.info} />
              <span className={css.infoText}>
                US & Canada numbers allowed only
              </span>
            </div>
          </div>
        </div>

        <div className={css.column}>
          <div className={css.subtitle}>Text message</div>

          <Editor
            styleMenubar={{ display: 'none' }}
            attributes={attributes}
            schema={schema}
            value={
              campaign?.soldConfigurations?.unicorn?.notifications?.sms?.content
            }
            onChange={state => setContent(state)}
          />
        </div>
        <div className={css.row}>
          <Button
            css={{ container: css.save }}
            onClick={onSave}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
          <Button color="transparent" onClick={close}>
            Back
          </Button>
        </div>
      </Menu.Content>
    </Menu>
  );
};
